import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfDay, startOfWeek, startOfMonth, endOfDay, endOfWeek, endOfMonth } from "date-fns";
import { fr } from "date-fns/locale";

const getPeriodStart = (period: string) => {
  const now = new Date();
  switch (period) {
    case "day":
      return startOfDay(now);
    case "week":
      return startOfWeek(now, { locale: fr });
    case "month":
      return startOfMonth(now);
    default:
      return null;
  }
};

const getPeriodEnd = (period: string) => {
  const now = new Date();
  switch (period) {
    case "day":
      return endOfDay(now);
    case "week":
      return endOfWeek(now, { locale: fr });
    case "month":
      return endOfMonth(now);
    default:
      return null;
  }
};

export const useTransactionData = (period: string) => {
  return useQuery({
    queryKey: ["transactions", period],
    queryFn: async () => {
      console.log("Fetching transactions for period:", period);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const periodStart = getPeriodStart(period);
      const periodEnd = getPeriodEnd(period);
      console.log("Period start date:", periodStart);
      console.log("Period end date:", periodEnd);

      // Construction de la requête de base
      let query = supabase
        .from("poultry_transactions")
        .select("*, credit_payments(*)")
        .eq("created_by", user.id)
        .order("created_at", { ascending: false });

      // Ajout du filtre de date si une période est spécifiée
      if (periodStart && periodEnd) {
        query = query
          .gte("created_at", periodStart.toISOString())
          .lte("created_at", periodEnd.toISOString());
      }

      const { data: salesData, error: salesError } = await query;
      
      if (salesError) {
        console.error("Error fetching sales:", salesError);
        throw salesError;
      }

      // Construction de la requête pour les retours
      let returnsQuery = supabase
        .from("poultry_transactions")
        .select("*")
        .eq("created_by", user.id)
        .eq("transaction_type", "return")
        .not("refund_amount", "eq", 0)
        .order("created_at", { ascending: false });

      // Ajout du filtre de date pour les retours si une période est spécifiée
      if (periodStart && periodEnd) {
        returnsQuery = returnsQuery
          .gte("created_at", periodStart.toISOString())
          .lte("created_at", periodEnd.toISOString());
      }

      const { data: returnsData, error: returnsError } = await returnsQuery;

      if (returnsError) {
        console.error("Error fetching returns:", returnsError);
        throw returnsError;
      }

      console.log("Fetched sales:", salesData?.length || 0);
      console.log("Fetched returns:", returnsData?.length || 0);

      return { 
        sales: salesData || [], 
        returns: returnsData || [] 
      };
    },
  });
};